/* eslint-disable import/no-import-module-exports */
/**
 * Create the store with asynchronously loaded reducers
 */

import { createStore, applyMiddleware, compose } from "redux";
import { fromJS } from "immutable";
import { routerMiddleware } from "react-router-redux";
import createSagaMiddleware from "redux-saga";
import * as Sentry from "@sentry/browser";
import createReducer from "./reducers";

const sagaMiddleware = createSagaMiddleware();

// Create Sentry Redux middleware
const sentryReduxEnhancer = createSentryReduxEnhancer();

// Function to create Sentry Redux enhancer
function createSentryReduxEnhancer() {
  return store => next => action => {
    try {
      // Track Redux actions as breadcrumbs in Sentry
      Sentry.addBreadcrumb({
        category: 'redux-action',
        message: action.type,
        level: 'info',
        data: {
          // Exclude potentially sensitive data or large payloads
          // This is a basic implementation - customize as needed for your application
          ...sanitizeAction(action),
        },
      });
      
      return next(action);
    } catch (error) {
      // Capture Redux errors
      Sentry.captureException(error, {
        extra: {
          action: sanitizeAction(action),
          state: sanitizeState(store.getState().toJS()),
        },
        tags: {
          'redux.action': action.type,
        },
      });
      throw error;
    }
  };
}

// Sanitize action for Sentry to avoid capturing sensitive data
function sanitizeAction(action) {
  // Don't include potentially sensitive action data
  const { type } = action;
  
  // These are examples of actions that might contain sensitive data
  const sensitiveActions = [
    'LOGIN_SUCCESS', 
    'UPDATE_PROFILE', 
    'SAVE_PAYMENT_METHOD',
    'AUTHENTICATE_USER'
  ];
  
  if (sensitiveActions.includes(type)) {
    return { type };
  }
  
  return action;
}

// Sanitize state for Sentry to avoid capturing sensitive data
function sanitizeState(state) {
  // Instead of capturing the full state, just capture a summary
  // to prevent envelope size limit issues
  const stateKeys = Object.keys(state);
  
  // Create a minimal state representation with just keys and types
  const summarizedState = {};
  
  stateKeys.forEach(key => {
    const value = state[key];
    // Check if value exists and isn't null
    if (value) {
      if (typeof value === 'object') {
        // Just record that this is an object and how many properties it has
        const size = Object.keys(value).length;
        // For large objects, just record the size to reduce payload
        if (size > 10) {
          summarizedState[key] = `[Object with ${size} properties]`;
        } else {
          // For smaller objects, include a bit more detail about keys
          summarizedState[key] = `[Object with keys: ${Object.keys(value).join(', ')}]`;
        }
      } else {
        // For primitive values, include the type
        summarizedState[key] = `[${typeof value}]`;
      }
    } else {
      summarizedState[key] = null;
    }
  });
  
  // Handle sensitive auth information if present
  if (state.auth) {
    summarizedState.auth = {
      isAuthenticated: state.auth.token ? true : false,
      hasUserInfo: state.auth.user ? true : false
    };
  }
  
  return summarizedState;
}

// eslint-disable-next-line default-param-last
export default function configureStore(initialState = {}, history) {
  // Create the store with three middlewares
  // 1. sagaMiddleware: Makes redux-sagas work
  // 2. routerMiddleware: Syncs the location/URL path to the state
  // 3. sentryReduxEnhancer: Tracks Redux actions and state for Sentry
  const middlewares = [
    sagaMiddleware,
    routerMiddleware(history),
    sentryReduxEnhancer,
  ];

  const enhancers = [applyMiddleware(...middlewares)];

  // If Redux DevTools Extension is installed use it, otherwise use Redux compose
  /* eslint-disable no-underscore-dangle */
  const composeEnhancers =
    process.env.NODE_ENV !== "production" &&
    typeof window === "object" &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;
  /* eslint-enable */

  const store = createStore(
    createReducer(),
    fromJS(initialState),
    composeEnhancers(...enhancers)
  );

  // Extensions
  store.runSaga = sagaMiddleware.run;
  store.asyncReducers = {}; // Async reducer registry

  // Make reducers hot reloadable, see http://mxs.is/googmo
  /* istanbul ignore next */
  if (module.hot) {
    module.hot.accept("./reducers", () => {
      import("./reducers").then((reducerModule) => {
        const createReducers = reducerModule.default;
        const nextReducers = createReducers(store.asyncReducers);

        store.replaceReducer(nextReducers);
      });
    });
  }

  return store;
}
